import { useState } from "react";
import { toast } from "sonner";
import {
  QrCode,
  Send,
  CheckCircle2,
  PhoneCall,
  MapPin,
  Building2,
  Users,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { apiPublic } from "@/lib/api";
import { VerifiedMobileInput } from "@/components/common/VerifiedInput";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    cafe_name: "",
    mobile: "",
    location: "",
    table_count: 10,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateMobile = (mob) => {
    const clean = mob.replace(/\D/g, "");
    return clean.length === 10 && /^[6-9]/.test(clean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.cafe_name.trim() || !form.location.trim()) {
      return toast.error("Please fill in all required fields.");
    }
    if (!validateMobile(form.mobile)) {
      return toast.error("Please enter a valid 10-digit Indian mobile number (e.g. 9876543210).");
    }
    if (form.table_count < 1) {
      return toast.error("Number of tables must be at least 1.");
    }

    setSubmitting(true);
    try {
      await apiPublic.post("/public/leads", form);
      setSubmitted(true);
      toast.success("Request submitted successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Left Side Content & Value Prop */}
        <div className="lg:col-span-5 space-y-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-mono font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Start Your Digital Transformation
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
              Let’s Create Your QR Menu
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Tell us about your cafe or restaurant and our team will contact you to set up your customized QR menu system within 48 hours.
            </p>
          </div>

          {/* Key Advantages Checklist */}
          <div className="bg-card border border-border rounded-3xl p-6 space-y-4 shadow-sm">
            <h3 className="font-heading text-lg font-bold text-slate-900">What You Get With Tablezy:</h3>
            <ul className="space-y-3 text-sm text-slate-600 font-medium">
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <span>Custom Brand Logo & Menu Design</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <span>Table-Assigned Downloadable QR Code Cards</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <span>Instant Menu & Price Update Portal</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                <span>Zero App Installation For Diners</span>
              </li>
            </ul>
          </div>

          {/* Quick Contact Card */}
          <div className="bg-slate-900 text-white rounded-3xl p-6 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary text-white grid place-items-center">
                <PhoneCall className="w-5 h-5 stroke-[2]" />
              </div>
              <div>
                <div className="text-xs text-slate-400 font-mono">Need Immediate Support?</div>
                <a href="tel:+919876543210" className="font-heading font-bold text-lg text-white hover:underline">
                  +91 98765 43210
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Form / Success State */}
        <div className="lg:col-span-7">
          <div className="bg-card border border-border rounded-3xl p-8 sm:p-10 shadow-xl relative overflow-hidden">
            {submitted ? (
              <div className="py-12 text-center space-y-6 animate-fade-up">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 text-emerald-500 grid place-items-center mx-auto">
                  <CheckCircle2 className="w-10 h-10 stroke-[2]" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-heading text-3xl font-bold text-slate-900">Thank You!</h2>
                  <p className="text-slate-600 max-w-md mx-auto text-base">
                    Your request has been received. Our team will contact you soon.
                  </p>
                </div>
                <button
                  onClick={() => setSubmitted(false)}
                  className="h-12 px-6 rounded-full border border-border hover:bg-slate-100 text-sm font-semibold transition-colors"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900">Request Your QR Menu</h2>
                  <p className="text-slate-500 text-xs mt-1">Fields marked with * are required.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Owner Name */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                      Owner Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                    />
                  </div>

                  {/* Cafe Name */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                      Cafe / Restaurant Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. The Urban Roast"
                      value={form.cafe_name}
                      onChange={(e) => setForm({ ...form, cafe_name: e.target.value })}
                      className="w-full h-12 px-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Verified Mobile Number Input */}
                  <VerifiedMobileInput
                    label="Mobile Number * (10-Digit Verification)"
                    value={form.mobile}
                    onChange={(val) => setForm({ ...form, mobile: val })}
                  />

                  {/* Table Count */}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                      Number of Tables *
                    </label>
                    <input
                      type="number"
                      required
                      min={1}
                      placeholder="e.g. 15"
                      value={form.table_count}
                      onChange={(e) => setForm({ ...form, table_count: parseInt(e.target.value) || 1 })}
                      className="w-full h-12 px-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-mono transition-all"
                    />
                  </div>
                </div>

                {/* Cafe Location */}
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                    Cafe Location (City & Area) *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      required
                      placeholder="e.g. Malviya Nagar, Jaipur"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      className="w-full h-12 pl-11 pr-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                    />
                  </div>
                </div>

                {/* Additional Message */}
                <div>
                  <label className="block text-xs font-mono uppercase tracking-wider text-slate-600 font-semibold mb-2">
                    Additional Requirements (Optional)
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Tell us anything else about your cafe, menu items, or launch timeline..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full p-4 rounded-xl border border-border bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 text-sm font-medium transition-all"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25 active:scale-95 transition-all disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  <span>{submitting ? "Submitting Request..." : "Request QR Menu"}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
